
        import { YouTubeMusicPlayer, RepeatMode } from './src/index.ts';
        
        let player;
        let isPlaylistOpen = false;
        
        // Available playlists
        const availablePlaylists = [
            { id: 'finest', name: '💀 Finest Music', file: 'playlist-finest-music.json', default: true },
            { id: 'bangers', name: '🔥 Bangers', file: 'playlist-bangers.json' },
            { id: 'mash', name: '🎵 Mash', file: 'playlist-mash.json' },
        ];
        
        // Find default playlist (fallback to first one)
        const defaultPlaylist = availablePlaylists.find(p => p.default) || availablePlaylists[0];
        let currentPlaylistId = defaultPlaylist.id;

        // Load tracklist from JSON file
        async function loadTracklist(playlistFile = null) {
            try {
                const file = playlistFile || availablePlaylists.find(p => p.id === currentPlaylistId)?.file || 'playlist-bangers.json';
                const response = await fetch(`./${file}`);
                const tracklist = await response.json();
                return tracklist;
            } catch (error) {
                console.error('Failed to load tracklist:', error);
                return [];
            }
        }

        // Calculate simple hash of tracklist for change detection
        function calculateTracklistHash(tracklist) {
            const str = JSON.stringify(tracklist.map(t => ({ id: t.id, title: t.title, artist: t.artist })));
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
            }
            return hash.toString();
        }

        // Check if tracklist has changed and clear storage if needed
        async function checkTracklistChanges() {
            try {
                const tracklist = await loadTracklist();
                const currentHash = calculateTracklistHash(tracklist);
                const storageKey = `ytMusicPlayer:tracklistHash:${currentPlaylistId}`;
                const storedHash = localStorage.getItem(storageKey);
                
                if (storedHash && storedHash !== currentHash) {
                    // Tracklist changed, clear stored data
                    console.log('Tracklist changed, clearing stored data...');
                    localStorage.removeItem('ytMusicPlayer:playerState');
                    localStorage.setItem(storageKey, currentHash);
                    return true; // Indicates data was cleared
                } else if (!storedHash) {
                    // First time, store hash
                    localStorage.setItem(storageKey, currentHash);
                }
                
                return false; // No changes
            } catch (error) {
                console.error('Failed to check tracklist changes:', error);
                return false;
            }
        }

        // Switch to a different playlist
        async function switchPlaylist(playlistId) {
            if (playlistId === currentPlaylistId) return;
            
            try {
                updateStatus('🔄 Switching playlist...');
                
                // Update current playlist
                currentPlaylistId = playlistId;
                
                // Clear current playlist
                player.clearPlaylist();
                
                // Load new tracklist
                await loadInitialTracks();
                
                // Update UI
                updateUI();
                updatePlaylistSelector();
                
                const playlistName = availablePlaylists.find(p => p.id === playlistId)?.name || 'Unknown';
                updateStatus(`✅ Switched to ${playlistName}!`);
                
                // Update URL to reflect playlist change
                updateBrowserURL();
                
                // Save session immediately on playlist change
                saveSession();
                
                // Handle track parameter if present (after playlist switch)
                if (window.urlTrackParam) {
                    setTimeout(() => {
                        jumpToTrackByName(window.urlTrackParam);
                        delete window.urlTrackParam;
                    }, 500);
                }
                
            } catch (error) {
                updateStatus('❌ Failed to switch playlist');
                console.error('Failed to switch playlist:', error);
            }
        }

        // Update playlist selector to reflect current playlist
        function updatePlaylistSelector() {
            const selector = document.getElementById('playlistSelector');
            if (selector) {
                selector.value = currentPlaylistId;
            }
        }
        
        // Initialize player
        async function initPlayer() {
            try {
                // Check if tracklist changed and clear storage if needed
                const tracklistChanged = await checkTracklistChanges();
                if (tracklistChanged) {
                    updateStatus('📋 Tracklist updated, loading new tracks...');
                }

                player = new YouTubeMusicPlayer({
                    containerId: 'youtube-player',
                    autoplay: false,
                    volume: 100, // Full volume - users control via system/browser
                    saveState: true
                });

                // Set up event listeners
                player.on('ready', async () => {
                    updateStatus('🎉 Player ready!');
                    
                    // Check build version and show notification if needed
                    checkBuildVersion();
                    
                    // Handle URL parameters first (they take priority over session restore)
                    await handleURLParameters();
                    // Note: CORS errors from doubleclick.net are normal (YouTube ads) and can be ignored
                    
                    // Only load initial tracks if no playlist exists (prevent duplicates on reload)
                    if (!player.hasInitializedPlaylist()) {
                        updateStatus('🎉 Player ready! Loading tracks...');
                        await loadInitialTracks();
                    } else {
                        updateStatus('🎉 Player ready! Playlist loaded from storage.');
                    }
                    
                    updateUI();
                    
                    // Handle track parameter if present (after playlist is loaded)
                    if (window.urlTrackParam) {
                        setTimeout(() => {
                            jumpToTrackByName(window.urlTrackParam);
                            delete window.urlTrackParam;
                        }, 500); // Small delay to ensure playlist is fully loaded
                    }
                    
                    // Restore session if no URL parameters were provided
                    if (!window.location.search) {
                        setTimeout(async () => {
                            await restoreSession();
                        }, 1500);
                    }
                    
                    // Update URL to reflect initial state (after small delay)
                    setTimeout(() => {
                        updateBrowserURL();
                    }, 2000);
                });

                player.on('trackChange', (data) => {
                    updateCurrentTrack(data.track);
                    updatePlaylist();
                    // Update URL to reflect track change
                    updateBrowserURL();
                    // Save session immediately on track change
                    saveSession();
                });

                player.on('stateChange', (data) => {
                    updatePlayButton(data.isPlaying);
                    updateStatusIndicator(data.isPlaying, data.isPaused);
                    if (data.isPlaying) {
                        updateStatus('▶ Playing...');
                    } else if (data.isPaused) {
                        updateStatus('⏸ Paused');
                    }
                });

                player.on('timeUpdate', (data) => {
                    updateProgress(data.currentTime, data.duration);
                    // Save progress with 30-second throttling
                    saveProgressThrottled();
                });

                player.on('playlistChange', () => {
                    updatePlaylist();
                });

                player.on('settingsChange', () => {
                    updateSettingsButtons();
                    // Save session immediately on settings change
                    saveSession();
                });

                player.on('error', (error) => {
                    if (error.type === 'VIDEO_NOT_EMBEDDABLE') {
                        updateStatus(`⏭️ Skipping non-embeddable track...`);
                    } else {
                        updateStatus(`❌ Error: ${error.message}`);
                    }
                    console.error('Player error:', error);
                });

            } catch (error) {
                updateStatus(`❌ Failed to initialize player: ${error.message}`);
                console.error('Initialization error:', error);
            }
        }

        // Load initial tracks from JSON
        async function loadInitialTracks() {
            try {
                const tracklist = await loadTracklist();
                let addedCount = 0;
                
                for (const trackData of tracklist) {
                    try {
                        const track = await player.addTrackWithMetadata(trackData);
                        if (track) {
                            addedCount++;
                        }
                    } catch (error) {
                        console.warn('Failed to add track:', trackData, error);
                    }
                }
                
                updateStatus(`✅ Loaded ${addedCount} tracks from tracklist!`);
            } catch (error) {
                updateStatus('❌ Failed to load initial tracks');
                console.error('Failed to load initial tracks:', error);
            }
        }

        // UI Functions
        function updateStatus(message) {
            console.log(message);
        }

        function updateStatusIndicator(isPlaying, isPaused) {
            const indicator = document.getElementById('statusIndicator');
            if (isPlaying) {
                indicator.className = 'w-3 h-3 bg-green-400 rounded-full animate-pulse-dot';
            } else if (isPaused) {
                indicator.className = 'w-3 h-3 bg-yellow-400 rounded-full';
            } else {
                indicator.className = 'w-3 h-3 bg-gray-400 rounded-full';
            }
        }

        // Handle album art load (simplified - always square)
        function handleAlbumArtLoad(img) {
            console.log(`Album art loaded: ${img.naturalWidth}x${img.naturalHeight}`);
            showAlbumArt(true);
        }

        // Show/hide album art based on load success
        function showAlbumArt(success) {
            const albumArt = document.getElementById('albumArt');
            const placeholder = document.getElementById('albumArtPlaceholder');
            const overlay = document.getElementById('albumArtOverlay');
            
            if (success) {
                albumArt.classList.remove('hidden');
                placeholder.classList.add('hidden');
                overlay.classList.remove('hidden');
            } else {
                // Randomly select one of the placeholder images (0-3)
                const randomIndex = Math.floor(Math.random() * 4);
                placeholder.src = `./placeholder_${randomIndex}.png`;
                
                albumArt.classList.add('hidden');
                placeholder.classList.remove('hidden');
                overlay.classList.add('hidden');
            }
        }

        // Update album art with YouTube thumbnail
        function updateAlbumArt(videoId) {
            if (!videoId) {
                showAlbumArt(false);
                return;
            }
            
            const albumArt = document.getElementById('albumArt');
            
            // Try different thumbnail qualities for better results
            // maxresdefault (1280x720) -> hqdefault (480x360) -> mqdefault (320x180)
            const thumbnailUrls = [
                `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
                `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
                `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
            ];
            
            // Try loading the highest quality first
            tryLoadThumbnail(albumArt, thumbnailUrls, 0);
        }

        // Recursively try loading thumbnails from highest to lowest quality
        function tryLoadThumbnail(imgElement, urls, index) {
            if (index >= urls.length) {
                // All URLs failed, show fallback
                showAlbumArt(false);
                return;
            }
            
            // Create a temporary image to test loading
            const testImg = new Image();
            testImg.onload = function() {
                // Success - use this URL
                imgElement.src = urls[index];
            };
            testImg.onerror = function() {
                // Failed - try next URL
                tryLoadThumbnail(imgElement, urls, index + 1);
            };
            testImg.src = urls[index];
        }

        function updateCurrentTrack(track) {
            const titleElement = document.getElementById('trackTitle');
            const infoElement = document.getElementById('trackInfo');
            
            if (track) {
                // Display: [artist] - [title]
                if (track.artist) {
                    titleElement.textContent = `${track.artist} - ${track.title}`;
                } else {
                    titleElement.textContent = track.title;
                }
                
                // Display tags
                if (track.tags && track.tags.length > 0) {
                    infoElement.textContent = track.tags.join(', ');
                } else {
                    infoElement.textContent = `Video ID: ${track.id}`;
                }
                
                // Update album art
                updateAlbumArt(track.id);
            } else {
                titleElement.textContent = 'No track loaded';
                infoElement.textContent = 'Select a track to start playing';
                showAlbumArt(false);
            }
        }

        function updatePlayButton(isPlaying) {
            const playIcon = document.getElementById('playIcon');
            const pauseIcon = document.getElementById('pauseIcon');
            
            if (isPlaying) {
                playIcon.classList.add('hidden');
                pauseIcon.classList.remove('hidden');
            } else {
                playIcon.classList.remove('hidden');
                pauseIcon.classList.add('hidden');
            }
        }

        function updateProgress(currentTime, duration) {
            const progressFill = document.getElementById('progressFill');
            const currentTimeEl = document.getElementById('currentTime');
            const durationEl = document.getElementById('duration');
            
            const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
            progressFill.style.width = progress + '%';
            
            currentTimeEl.textContent = formatTime(currentTime);
            durationEl.textContent = formatTime(duration);
        }

        function updatePlaylist() {
            const trackList = document.getElementById('trackList');
            const trackCount = document.getElementById('trackCount');
            const playlist = player.getPlaylist();
            
            trackCount.textContent = playlist.tracks.length;
            
            if (playlist.tracks.length === 0) {
                trackList.innerHTML = `
                    <div class="flex items-center justify-center p-8 text-gray-500">
                        <div class="text-center">
                            <svg class="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
                            </svg>
                            <p class="text-sm">Loading tracks...</p>
                        </div>
                    </div>
                `;
                return;
            }
            
            trackList.innerHTML = playlist.tracks.map((track, index) => {
                const isCurrent = index === playlist.currentIndex;
                
                let artist = '';
                if (track.artist) {
                    artist = track.artist;
                } else if (track.tags && track.tags.length > 0) {
                    artist = track.tags.slice(0, 2).join(', ');
                }
                
                const duration = track.duration > 0 ? formatTime(track.duration) : '';
                
                return `
                    <div class="playlist-item p-4 cursor-pointer ${isCurrent ? 'active' : ''}" onclick="jumpToTrack(${index})">
                        <div class="flex items-center gap-3">
                            <div class="w-8 h-8 flex items-center justify-center">
                                ${isCurrent ? `
                                    <div class="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                        <div class="w-2 h-2 bg-white rounded-full animate-pulse-dot"></div>
                                    </div>
                                ` : `
                                    <svg class="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z"/>
                                    </svg>
                                `}
                            </div>
                            <div class="flex-1 min-w-0">
                                <h4 class="track-title font-medium text-sm truncate ${isCurrent ? 'text-red-600' : 'text-gray-900'}">${track.title}</h4>
                                <p class="text-xs text-gray-500 truncate">${artist}</p>
                            </div>
                            ${duration ? `<span class="text-xs text-gray-400">${duration}</span>` : ''}
                        </div>
                    </div>
                `;
            }).join('');
        }

        function updateSettingsButtons() {
            const settings = player.getSettings();
            const shuffleBtn = document.getElementById('shuffleBtn');
            const repeatBtn = document.getElementById('repeatBtn');
            
            // Update shuffle button
            if (settings.shuffled) {
                shuffleBtn.classList.add('text-red-500');
                shuffleBtn.classList.remove('text-gray-600');
            } else {
                shuffleBtn.classList.add('text-gray-600');
                shuffleBtn.classList.remove('text-red-500');
            }
            
            // Update repeat button
            if (settings.repeatMode !== RepeatMode.NONE) {
                repeatBtn.classList.add('text-red-500');
                repeatBtn.classList.remove('text-gray-600');
            } else {
                repeatBtn.classList.add('text-gray-600');
                repeatBtn.classList.remove('text-red-500');
            }
        }

        function updateUI() {
            updatePlaylist();
            updateSettingsButtons();
            updateCurrentTrack(player.getCurrentTrack());
            updatePlaylistSelector();
        }

        function formatTime(seconds) {
            if (isNaN(seconds) || seconds < 0) return '0:00';
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        }

        // Control Functions
        window.togglePlay = function() {
            player.togglePlay();
        };

        window.switchPlaylist = switchPlaylist;

        window.next = function() {
            player.next();
        };

        window.previous = function() {
            player.previous();
        };

        window.seek = function(event) {
            const progressBar = event.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const percentage = x / rect.width;
            const duration = player.getDuration();
            const seekTime = percentage * duration;
            
            player.seekTo(seekTime);
        };

        window.toggleShuffle = function() {
            player.toggleShuffle();
        };

        window.toggleRepeat = function() {
            player.toggleRepeat();
        };

        window.jumpToTrack = function(index) {
            player.jumpToTrack(index);
        };

        window.togglePlaylist = function() {
            const panel = document.getElementById('playlistPanel');
            isPlaylistOpen = !isPlaylistOpen;
            
            if (isPlaylistOpen) {
                panel.classList.remove('hidden');
            } else {
                panel.classList.add('hidden');
            }
        };

        window.toggleLike = function() {
            const track = player.getCurrentTrack();
            if (track) {
                const youtubeUrl = `https://www.youtube.com/watch?v=${track.id}`;
                window.open(youtubeUrl, '_blank');
            }
        };

        window.copyTrackInfo = function() {
            const track = player.getCurrentTrack();
            if (track) {
                const youtubeUrl = `https://www.youtube.com/watch?v=${track.id}`;
                navigator.clipboard.writeText(youtubeUrl).then(() => {
                    console.log('📋 YouTube URL copied to clipboard!');
                }).catch(err => {
                    console.error('Failed to copy to clipboard:', err);
                });
            }
        };

        // Video mode toggle functionality
        let isVideoMode = localStorage.getItem('ytMusicPlayer:videoMode') === 'true';
        
        window.toggleVideoMode = function() {
            isVideoMode = !isVideoMode;
            const body = document.body;
            const videoOffIcon = document.getElementById('videoOffIcon');
            const videoOnIcon = document.getElementById('videoOnIcon');
            
            if (isVideoMode) {
                body.classList.add('video-mode');
                videoOffIcon.classList.add('hidden');
                videoOnIcon.classList.remove('hidden');
                console.log('🎬 Video mode enabled');
            } else {
                body.classList.remove('video-mode');
                videoOffIcon.classList.remove('hidden');
                videoOnIcon.classList.add('hidden');
                console.log('🖼️ Album art mode enabled');
            }
            
            // Save preference
            localStorage.setItem('ytMusicPlayer:videoMode', isVideoMode.toString());
            
            // Update URL to reflect video mode change
            updateBrowserURL();
        };
        
        // Initialize video mode state
        function initVideoMode() {
            if (isVideoMode) {
                toggleVideoMode();
            }
        }

        // Parse URL parameters for deep linking
        function parseURLParameters() {
            const urlParams = new URLSearchParams(window.location.search);
            return {
                playlist: urlParams.get('playlist'),
                track: urlParams.get('track'), 
                video: urlParams.get('video') === 'true'
            };
        }

        // Handle URL parameters for deep linking
        async function handleURLParameters() {
            const params = parseURLParameters();
            
            // Handle video mode parameter
            if (params.video && !isVideoMode) {
                console.log('🎬 Enabling video mode from URL parameter');
                toggleVideoMode();
            }
            
            // Handle playlist parameter
            if (params.playlist) {
                const playlistExists = availablePlaylists.find(p => p.id === params.playlist);
                if (playlistExists && params.playlist !== currentPlaylistId) {
                    console.log(`📋 Loading playlist from URL: ${params.playlist}`);
                    await switchPlaylist(params.playlist);
                }
            }
            
            // Handle track parameter - will be implemented after playlist loads
            if (params.track) {
                // Store for later use when playlist is ready
                window.urlTrackParam = params.track;
            }
        }

        // Jump to track by name or ID (for URL parameter)
        function jumpToTrackByName(trackIdentifier) {
            const playlist = player.getPlaylist();
            if (!playlist || !playlist.tracks.length) {
                console.warn('No playlist loaded for track jump');
                return false;
            }
            
            // Try to find track by title (case insensitive) or by video ID
            const trackIndex = playlist.tracks.findIndex(track => 
                track.title.toLowerCase().includes(trackIdentifier.toLowerCase()) ||
                track.id === trackIdentifier ||
                (track.artist && `${track.artist} - ${track.title}`.toLowerCase().includes(trackIdentifier.toLowerCase()))
            );
            
            if (trackIndex !== -1) {
                console.log(`🎵 Jumping to track: ${playlist.tracks[trackIndex].title}`);
                player.jumpToTrack(trackIndex);
                return true;
            } else {
                console.warn(`Track not found: ${trackIdentifier}`);
                return false;
            }
        }

        // Update browser URL to reflect current state
        function updateBrowserURL() {
            const currentTrack = player?.getCurrentTrack();
            const params = new URLSearchParams();
            
            // Add playlist parameter if not default
            if (currentPlaylistId !== defaultPlaylist.id) {
                params.set('playlist', currentPlaylistId);
            }
            
            // Add track parameter if a track is playing
            if (currentTrack && currentTrack.title) {
                // Use a sanitized version of the track title for readability
                const trackParam = currentTrack.title
                    .toLowerCase()
                    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
                    .replace(/\s+/g, '-') // Replace spaces with dashes
                    .substring(0, 50); // Limit length
                params.set('track', trackParam);
            }
            
            // Add video parameter if video mode is enabled
            if (isVideoMode) {
                params.set('video', 'true');
            }
            
            // Build the new URL
            const baseUrl = window.location.pathname;
            const queryString = params.toString();
            const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
            
            // Update the URL without reloading the page
            window.history.replaceState({}, '', newUrl);
        }

        // Enhanced Session Management System
        const SESSION_KEY = 'ytPlayer:session';
        const BUILD_TIME_KEY = 'ytPlayer:buildTime';
        let lastProgressSave = 0; // Timestamp of last progress save
        let sessionData = {};

        // Save session data to localStorage
        function saveSession() {
            try {
                const currentTrack = player?.getCurrentTrack();
                const playlist = player?.getPlaylist();
                const settings = player?.getSettings();
                
                sessionData = {
                    playlist: currentPlaylistId,
                    track: currentTrack?.id || null,
                    shuffle: settings?.shuffled || false,
                    repeat: settings?.repeatMode || 'none',
                    video: isVideoMode,
                    progress: player?.getCurrentTime() || 0,
                    volume: player?.getVolume() || 100,
                    lastSaved: Date.now()
                };
                
                localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
                console.log('💾 Session saved');
            } catch (error) {
                console.warn('Failed to save session:', error);
            }
        }

        // Save progress with 30-second throttling
        function saveProgressThrottled() {
            const now = Date.now();
            if (now - lastProgressSave >= 30000) { // 30 seconds
                lastProgressSave = now;
                saveSession();
            }
        }

        // Load session data from localStorage
        function loadSession() {
            try {
                const stored = localStorage.getItem(SESSION_KEY);
                if (stored) {
                    return JSON.parse(stored);
                }
            } catch (error) {
                console.warn('Failed to load session:', error);
            }
            return null;
        }

        // Restore session state
        async function restoreSession() {
            const session = loadSession();
            if (!session) {
                console.log('No session to restore');
                return;
            }

            console.log('🔄 Restoring session...', session);

            try {
                // Restore playlist if different
                if (session.playlist && session.playlist !== currentPlaylistId) {
                    await switchPlaylist(session.playlist);
                }

                // Wait for playlist to load
                await new Promise(resolve => setTimeout(resolve, 500));

                // Restore track
                if (session.track) {
                    const playlist = player.getPlaylist();
                    const trackIndex = playlist.tracks.findIndex(track => track.id === session.track);
                    if (trackIndex !== -1) {
                        player.jumpToTrack(trackIndex);
                        
                        // Restore progress after track loads
                        setTimeout(() => {
                            if (session.progress > 0) {
                                player.seekTo(session.progress);
                                console.log(`⏱️ Restored progress: ${Math.floor(session.progress)}s`);
                            }
                        }, 1000);
                    }
                }

                // Restore settings
                if (session.shuffle !== undefined) {
                    const currentSettings = player.getSettings();
                    if (currentSettings.shuffled !== session.shuffle) {
                        player.toggleShuffle();
                    }
                }

                // Restore video mode
                if (session.video !== isVideoMode) {
                    toggleVideoMode();
                }

                console.log('✅ Session restored successfully');
            } catch (error) {
                console.warn('Failed to restore session:', error);
            }
        }

        // Build notification system
        function checkBuildVersion() {
            try {
                const currentBuildTime = document.querySelector('meta[name="build-time"]')?.content;
                if (!currentBuildTime) {
                    console.log('No build time found in meta tag');
                    return;
                }

                const storedBuildTime = localStorage.getItem(BUILD_TIME_KEY);
                const dismissKey = `ytPlayer:buildDismissed:${currentBuildTime}`;
                const isDismissed = localStorage.getItem(dismissKey) === 'true';

                if (storedBuildTime && storedBuildTime !== currentBuildTime && !isDismissed) {
                    showBuildNotification(currentBuildTime);
                }

                // Always update stored build time
                localStorage.setItem(BUILD_TIME_KEY, currentBuildTime);
            } catch (error) {
                console.warn('Failed to check build version:', error);
            }
        }

        function showBuildNotification(buildTime) {
            const notification = document.getElementById('buildNotification');
            const notificationText = document.getElementById('buildNotificationText');
            
            notificationText.textContent = `${buildTime} New version available - more bangers, more tracks`;
            
            // Show notification
            setTimeout(() => {
                notification.classList.add('show');
                document.body.classList.add('notification-shown');
            }, 500);
        }

        window.dismissBuildNotification = function() {
            const notification = document.getElementById('buildNotification');
            const currentBuildTime = document.querySelector('meta[name="build-time"]')?.content;
            
            // Hide notification
            notification.classList.remove('show');
            document.body.classList.remove('notification-shown');
            
            // Mark as dismissed for this build
            if (currentBuildTime) {
                localStorage.setItem(`ytPlayer:buildDismissed:${currentBuildTime}`, 'true');
            }
        };

        // Make album art functions globally available for HTML callbacks
        window.showAlbumArt = showAlbumArt;
        window.handleAlbumArtLoad = handleAlbumArtLoad;

        // Initialize player when page loads
        initPlayer();
        initVideoMode();
    